// Short synthesised sound cues for the diorama's acts (docs/PLAN.md "Act visibility" §6):
// no files to ship, offline, tiny. Off when Settings → Sound is off. Most phones are muted,
// so these are a bonus; the visual cues (marker, timing, katakana) must work on their own.
//
// Places (docs/PLACES.md) add: act recipes registered by name with `recipes()` (the diorama
// engine's engine/sfx.js, ported in map/placekit/sfx.ts, plus each place's own), stereo pan per
// call (`play(cue, { pan })`), and decoded files (picked sound files, voice clips) via
// `playBuffer()`. Everything goes through one master gain; recipes connect to `out()`.
import { useSettings } from '../data/settings'

export type Cue =
  | 'whoosh'
  | 'thud'
  | 'pop'
  | 'roar'
  | 'cheer'
  | 'rustle'
  | 'chime'
  | 'bell'
  | 'ding'
  | 'toot'
  | 'splash'
  | 'caw'
  | 'shatter'
  | 'rumble'
  | 'slurp'
  | 'squeak'
  | 'gong'
  | 'taiko'
  | 'horn'
  | 'boing'
  | 'sizzle'
  | 'clap'

/** A recipe: (audio context, start time, volume); it connects to `out()`. */
export type Recipe = (a: BaseAudioContext, t: number, v: number) => void

export interface PlayOpts {
  /** -1 (left) … 1 (right). */
  pan?: number
  volume?: number
  /** Seconds from now. */
  delay?: number
}

let ctx: AudioContext | null = null
let master: GainNode | null = null
/** Where cues connect during one play() call: its panner (or the master). */
let DEST: AudioNode | null = null

function audio(): AudioContext | null {
  if (!useSettings.getState().sound) return null
  try {
    ctx ??= new AudioContext()
    if (ctx.state === 'suspended') void ctx.resume()
    return ctx
  } catch {
    return null
  }
}

/** The running audio context, if sound is on (null otherwise). */
export function audioContext(): AudioContext | null {
  return audio()
}

/** Call from a user gesture once (first tap on the Map) so later cues may play. */
export function unlockAudio() {
  audio()
}

/**
 * WI-25: a limiter on the master, so no cue, picked take or voice clips (526 effects peak above
 * −1 dBTP: npm run audio:report) and two sounds at once don't add up to a crackle. A fast,
 * hard-kneed compressor just under full scale: it only acts on peaks. (The trims stay the owner's
 * call after listening; this is the safety net in the app.)
 */
export const LIMITER = { threshold: -3, knee: 0, ratio: 20, attack: 0.002, release: 0.2 }
/**
 * After the limiter: a DynamicsCompressor adds its own make-up gain (about +1.7 dB here) and isn't
 * a brick wall, so the bus comes down below full scale before the speaker (no clicks on boosted
 * clips' transients).
 */
export const LIMITER_OUT = 0.8

/** The master bus: master gain → limiter → output trim → speakers. Exported for tests. */
export function masterOf(a: BaseAudioContext): GainNode {
  if (!master || master.context !== a) {
    master = a.createGain()
    master.gain.value = 0.9
    const lim = a.createDynamicsCompressor()
    lim.threshold.value = LIMITER.threshold
    lim.knee.value = LIMITER.knee
    lim.ratio.value = LIMITER.ratio
    lim.attack.value = LIMITER.attack
    lim.release.value = LIMITER.release
    const trim = a.createGain()
    trim.gain.value = LIMITER_OUT
    master.connect(lim).connect(trim).connect(a.destination)
  }
  return master
}

/** Decoded files, newest last; the oldest go past this many (card clips are a few KB each). */
const BUFFER_CAP = 48
const buffers = new Map<string, Promise<AudioBuffer | null>>()

/** A file decoded once and kept (a small LRU), or null when it can't be fetched or decoded. */
function bufferOf(a: BaseAudioContext, url: string): Promise<AudioBuffer | null> {
  let b = buffers.get(url)
  if (b) buffers.delete(url)
  else
    b = fetch(url)
      .then((r) => (r.ok ? r.arrayBuffer() : null))
      .then((ab) => (ab ? a.decodeAudioData(ab) : null))
      .catch(() => null)
  buffers.set(url, b)
  while (buffers.size > BUFFER_CAP) buffers.delete(buffers.keys().next().value!)
  return b
}

export interface Playing {
  stop(): void
}

/**
 * WI-25: plays a sound file through the master bus (and so the limiter), `gainDb` louder or
 * quieter: card clips are about 10 dB under the places' voices, which an <audio> element's volume
 * (at most 1) can't make up. Decoded once and played as a one-shot buffer source, whose nodes are
 * let go when it ends (an <audio> per clip with its own source node never was). Null when there's
 * no running audio context (sound off, not unlocked yet) or the file won't load: the caller then
 * falls back (a plain <audio>, the phone's voice, a synth cue).
 */
export async function playFile(url: string, opts: { gainDb?: number; pan?: number } = {}): Promise<Playing | null> {
  const a = audio()
  if (!a || a.state !== 'running') return null
  const buf = await bufferOf(a, url)
  if (!buf) return null
  const s = playBuffer(buf, { volume: 10 ** ((opts.gainDb ?? 0) / 20), pan: opts.pan })
  return s
    ? {
        stop: () => {
          try {
            s.stop()
          } catch {
            // already stopped
          }
        },
      }
    : null
}

/** The node a cue connects to right now. */
function dest(): AudioNode {
  return DEST ?? masterOf(ctx!)
}

/** The connection point recipes use (the current call's panner, or the master). */
export function out(): AudioNode {
  return dest()
}

export function noise(a: BaseAudioContext, seconds: number): AudioBufferSourceNode {
  const buf = a.createBuffer(1, Math.ceil(a.sampleRate * seconds), a.sampleRate)
  const d = buf.getChannelData(0)
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1
  const s = a.createBufferSource()
  s.buffer = buf
  return s
}

export function env(a: BaseAudioContext, peak: number, attack: number, decay: number): GainNode {
  const g = a.createGain()
  const t = a.currentTime
  g.gain.setValueAtTime(0.0001, t)
  g.gain.exponentialRampToValueAtTime(peak, t + attack)
  g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay)
  g.connect(dest())
  return g
}

function playBuiltin(cue: string, volume = 1) {
  const a = audio()
  if (!a || a.state !== 'running') return
  const t = a.currentTime
  const v = 0.35 * volume
  switch (cue) {
    case 'whoosh': {
      const n = noise(a, 0.5)
      const f = a.createBiquadFilter()
      f.type = 'bandpass'
      f.Q.value = 2
      f.frequency.setValueAtTime(400, t)
      f.frequency.exponentialRampToValueAtTime(2400, t + 0.35)
      n.connect(f).connect(env(a, v * 0.7, 0.08, 0.38))
      n.start()
      break
    }
    case 'thud': {
      const o = a.createOscillator()
      o.frequency.setValueAtTime(140, t)
      o.frequency.exponentialRampToValueAtTime(45, t + 0.25)
      o.connect(env(a, v * 1.4, 0.005, 0.3))
      o.start()
      o.stop(t + 0.35)
      const n = noise(a, 0.25)
      const f = a.createBiquadFilter()
      f.type = 'lowpass'
      f.frequency.value = 900
      n.connect(f).connect(env(a, v * 0.6, 0.005, 0.22))
      n.start()
      break
    }
    case 'pop': {
      const o = a.createOscillator()
      o.type = 'triangle'
      o.frequency.setValueAtTime(500, t)
      o.frequency.exponentialRampToValueAtTime(1200, t + 0.08)
      o.connect(env(a, v * 0.8, 0.005, 0.12))
      o.start()
      o.stop(t + 0.15)
      break
    }
    case 'roar': {
      const o = a.createOscillator()
      o.type = 'sawtooth'
      o.frequency.setValueAtTime(95, t)
      o.frequency.linearRampToValueAtTime(70, t + 0.9)
      const lfo = a.createOscillator()
      const depth = a.createGain()
      lfo.frequency.value = 22
      depth.gain.value = 18
      lfo.connect(depth).connect(o.frequency)
      const f = a.createBiquadFilter()
      f.type = 'lowpass'
      f.frequency.value = 700
      o.connect(f).connect(env(a, v * 1.1, 0.12, 0.9))
      o.start()
      lfo.start()
      o.stop(t + 1.1)
      lfo.stop(t + 1.1)
      break
    }
    case 'cheer': {
      for (const [i, hz] of [523, 659, 784].entries()) {
        const o = a.createOscillator()
        o.type = 'triangle'
        o.frequency.value = hz
        const g = a.createGain()
        g.gain.setValueAtTime(0.0001, t + i * 0.07)
        g.gain.exponentialRampToValueAtTime(v * 0.45, t + i * 0.07 + 0.02)
        g.gain.exponentialRampToValueAtTime(0.0001, t + i * 0.07 + 0.25)
        g.connect(dest())
        o.connect(g)
        o.start(t + i * 0.07)
        o.stop(t + i * 0.07 + 0.3)
      }
      break
    }
    case 'rustle': {
      const n = noise(a, 0.6)
      const f = a.createBiquadFilter()
      f.type = 'highpass'
      f.frequency.value = 2500
      n.connect(f).connect(env(a, v * 0.5, 0.05, 0.5))
      n.start()
      break
    }
    case 'chime': {
      const o = a.createOscillator()
      o.type = 'sine'
      o.frequency.value = 1318
      o.connect(env(a, v * 0.5, 0.005, 0.6))
      o.start()
      o.stop(t + 0.7)
      break
    }
    case 'bell': {
      // A big clock bell: a low partial plus an inharmonic one, long ring.
      for (const [hz, k] of [[392, 0.6], [392 * 2.76, 0.25], [392 * 5.4, 0.1]] as const) {
        const o = a.createOscillator()
        o.type = 'sine'
        o.frequency.value = hz
        o.connect(env(a, v * k, 0.004, 1.6))
        o.start()
        o.stop(t + 1.7)
      }
      break
    }
    case 'ding': {
      // A streetcar's chin-chin: two quick strikes.
      for (const d of [0, 0.16]) {
        const o = a.createOscillator()
        o.type = 'triangle'
        o.frequency.value = 1760
        const g = a.createGain()
        g.gain.setValueAtTime(0.0001, t + d)
        g.gain.exponentialRampToValueAtTime(v * 0.5, t + d + 0.005)
        g.gain.exponentialRampToValueAtTime(0.0001, t + d + 0.3)
        g.connect(dest())
        o.connect(g)
        o.start(t + d)
        o.stop(t + d + 0.32)
      }
      break
    }
    case 'toot': {
      // A steam whistle: a breathy chord that rises a little.
      for (const hz of [587, 740, 880]) {
        const o = a.createOscillator()
        o.type = 'sawtooth'
        o.frequency.setValueAtTime(hz * 0.97, t)
        o.frequency.linearRampToValueAtTime(hz, t + 0.15)
        const f = a.createBiquadFilter()
        f.type = 'lowpass'
        f.frequency.value = 1800
        o.connect(f).connect(env(a, v * 0.18, 0.06, 0.9))
        o.start()
        o.stop(t + 1.0)
      }
      const n = noise(a, 1)
      const f = a.createBiquadFilter()
      f.type = 'bandpass'
      f.frequency.value = 3000
      n.connect(f).connect(env(a, v * 0.2, 0.05, 0.9))
      n.start()
      break
    }
    case 'splash': {
      const n = noise(a, 0.6)
      const f = a.createBiquadFilter()
      f.type = 'lowpass'
      f.frequency.setValueAtTime(3000, t)
      f.frequency.exponentialRampToValueAtTime(400, t + 0.5)
      n.connect(f).connect(env(a, v * 0.8, 0.01, 0.5))
      n.start()
      break
    }
    case 'caw': {
      // A crow or a gull: a nasal squawk that bends down.
      for (const d of [0, 0.28]) {
        const o = a.createOscillator()
        o.type = 'sawtooth'
        o.frequency.setValueAtTime(900, t + d)
        o.frequency.exponentialRampToValueAtTime(520, t + d + 0.2)
        const f = a.createBiquadFilter()
        f.type = 'bandpass'
        f.frequency.value = 1400
        f.Q.value = 3
        const g = a.createGain()
        g.gain.setValueAtTime(0.0001, t + d)
        g.gain.exponentialRampToValueAtTime(v * 0.6, t + d + 0.02)
        g.gain.exponentialRampToValueAtTime(0.0001, t + d + 0.22)
        g.connect(dest())
        o.connect(f).connect(g)
        o.start(t + d)
        o.stop(t + d + 0.25)
      }
      break
    }
    case 'shatter': {
      const n = noise(a, 0.5)
      const f = a.createBiquadFilter()
      f.type = 'highpass'
      f.frequency.value = 4000
      n.connect(f).connect(env(a, v * 0.7, 0.002, 0.4))
      n.start()
      for (const [i, hz] of [2637, 3520, 3136, 4186].entries()) {
        const o = a.createOscillator()
        o.frequency.value = hz
        const g = a.createGain()
        g.gain.setValueAtTime(0.0001, t + i * 0.05)
        g.gain.exponentialRampToValueAtTime(v * 0.18, t + i * 0.05 + 0.003)
        g.gain.exponentialRampToValueAtTime(0.0001, t + i * 0.05 + 0.25)
        g.connect(dest())
        o.connect(g)
        o.start(t + i * 0.05)
        o.stop(t + i * 0.05 + 0.3)
      }
      break
    }
    case 'rumble': {
      const o = a.createOscillator()
      o.type = 'sawtooth'
      o.frequency.value = 48
      const lfo = a.createOscillator()
      const depth = a.createGain()
      lfo.frequency.value = 9
      depth.gain.value = 8
      lfo.connect(depth).connect(o.frequency)
      const f = a.createBiquadFilter()
      f.type = 'lowpass'
      f.frequency.value = 300
      o.connect(f).connect(env(a, v * 0.9, 0.15, 1.2))
      o.start()
      lfo.start()
      o.stop(t + 1.4)
      lfo.stop(t + 1.4)
      break
    }
    case 'slurp': {
      const n = noise(a, 0.7)
      const f = a.createBiquadFilter()
      f.type = 'bandpass'
      f.Q.value = 6
      f.frequency.setValueAtTime(600, t)
      f.frequency.exponentialRampToValueAtTime(2200, t + 0.6)
      n.connect(f).connect(env(a, v * 0.9, 0.05, 0.6))
      n.start()
      break
    }
    case 'squeak': {
      const o = a.createOscillator()
      o.type = 'triangle'
      o.frequency.setValueAtTime(1400, t)
      o.frequency.exponentialRampToValueAtTime(2100, t + 0.06)
      o.frequency.exponentialRampToValueAtTime(1100, t + 0.18)
      o.connect(env(a, v * 0.5, 0.005, 0.2))
      o.start()
      o.stop(t + 0.25)
      break
    }
    case 'gong': {
      // A temple bell (bonshō): very low, long, slightly beating.
      for (const [hz, k] of [[110, 0.7], [112.5, 0.5], [110 * 2.4, 0.2], [110 * 4.1, 0.08]] as const) {
        const o = a.createOscillator()
        o.type = 'sine'
        o.frequency.value = hz
        o.connect(env(a, v * k, 0.01, 3.2))
        o.start()
        o.stop(t + 3.3)
      }
      break
    }
    case 'taiko': {
      for (const d of [0, 0.22]) {
        const o = a.createOscillator()
        o.frequency.setValueAtTime(110, t + d)
        o.frequency.exponentialRampToValueAtTime(50, t + d + 0.3)
        const g = a.createGain()
        g.gain.setValueAtTime(0.0001, t + d)
        g.gain.exponentialRampToValueAtTime(v * 1.5, t + d + 0.005)
        g.gain.exponentialRampToValueAtTime(0.0001, t + d + 0.4)
        g.connect(dest())
        o.connect(g)
        o.start(t + d)
        o.stop(t + d + 0.45)
      }
      break
    }
    case 'horn': {
      // A ferry / train horn: two low saws.
      for (const hz of [196, 247]) {
        const o = a.createOscillator()
        o.type = 'sawtooth'
        o.frequency.value = hz
        const f = a.createBiquadFilter()
        f.type = 'lowpass'
        f.frequency.value = 900
        o.connect(f).connect(env(a, v * 0.25, 0.05, 1.1))
        o.start()
        o.stop(t + 1.2)
      }
      break
    }
    case 'boing': {
      const o = a.createOscillator()
      o.type = 'triangle'
      o.frequency.setValueAtTime(180, t)
      o.frequency.exponentialRampToValueAtTime(520, t + 0.12)
      o.frequency.exponentialRampToValueAtTime(240, t + 0.45)
      const lfo = a.createOscillator()
      const depth = a.createGain()
      lfo.frequency.value = 14
      depth.gain.value = 40
      lfo.connect(depth).connect(o.frequency)
      o.connect(env(a, v * 0.8, 0.005, 0.5))
      o.start()
      lfo.start()
      o.stop(t + 0.55)
      lfo.stop(t + 0.55)
      break
    }
    case 'sizzle': {
      const n = noise(a, 1.0)
      const f = a.createBiquadFilter()
      f.type = 'highpass'
      f.frequency.value = 5000
      n.connect(f).connect(env(a, v * 0.4, 0.05, 0.9))
      n.start()
      break
    }
    case 'clap': {
      for (let i = 0; i < 6; i++) {
        const d = i * 0.09 + Math.random() * 0.03
        const n = noise(a, 0.08)
        const f = a.createBiquadFilter()
        f.type = 'bandpass'
        f.frequency.value = 1500
        const g = a.createGain()
        g.gain.setValueAtTime(0.0001, t + d)
        g.gain.exponentialRampToValueAtTime(v * 0.6, t + d + 0.003)
        g.gain.exponentialRampToValueAtTime(0.0001, t + d + 0.07)
        g.connect(dest())
        n.connect(f).connect(g)
        n.start(t + d)
      }
      break
    }
  }
}

const BUILTIN = new Set<string>(['whoosh', 'thud', 'pop', 'roar', 'cheer', 'rustle', 'chime', 'bell', 'ding', 'toot', 'splash', 'caw', 'shatter', 'rumble', 'slurp', 'squeak', 'gong', 'taiko', 'horn', 'boing', 'sizzle', 'clap'])
const RECIPES: Record<string, Recipe> = {}

/** Registers named act recipes (the shared Japan set and each place's own). */
export function recipes(map: Record<string, Recipe>) {
  Object.assign(RECIPES, map)
}

/** True if `cue` names a built-in cue or a registered recipe. */
export function hasCue(cue: string): boolean {
  return cue in RECIPES || BUILTIN.has(cue)
}

/**
 * Plays a cue: a built-in name, a registered recipe, or a list of [cue, delaySeconds] steps.
 * The second argument is the volume (the Hokkaido islands' form) or { pan, volume, delay }.
 */
export function play(cue: string | readonly (readonly [string, number])[], opts: number | PlayOpts = {}) {
  const o: PlayOpts = typeof opts === 'number' ? { volume: opts } : opts
  const a = audio()
  if (!a || a.state !== 'running' || !cue) return
  if (typeof cue !== 'string') {
    for (const [c, d] of cue) play(c, { ...o, delay: (o.delay ?? 0) + d })
    return
  }
  const go = () => {
    const m = masterOf(a)
    const prev = DEST
    let node: AudioNode = m
    if (o.pan) {
      const p = a.createStereoPanner()
      p.pan.value = Math.max(-1, Math.min(1, o.pan))
      p.connect(m)
      node = p
    }
    DEST = node
    try {
      const r = RECIPES[cue]
      if (r) r(a, a.currentTime, 0.35 * (o.volume ?? 1))
      else playBuiltin(cue, o.volume ?? 1)
    } finally {
      DEST = prev
    }
  }
  if (o.delay) setTimeout(go, o.delay * 1000)
  else go()
}

/** Plays a decoded clip (a picked sound file or a voice line) through the master, panned. */
export function playBuffer(buf: AudioBuffer, opts: PlayOpts = {}): AudioBufferSourceNode | null {
  const a = audio()
  if (!a || a.state !== 'running') return null
  const m = masterOf(a)
  const s = a.createBufferSource()
  s.buffer = buf
  const g = a.createGain()
  g.gain.value = opts.volume ?? 1
  let node: AudioNode = g
  if (opts.pan) {
    const p = a.createStereoPanner()
    p.pan.value = Math.max(-1, Math.min(1, opts.pan * 0.6))
    g.connect(p)
    node = p
  }
  node.connect(m)
  s.connect(g)
  // Let the chain go when it's done (or stopped).
  s.onended = () => {
    s.disconnect()
    g.disconnect()
    if (node !== g) node.disconnect()
  }
  s.start(a.currentTime + (opts.delay ?? 0))
  return s
}

/** Test helper: renders a recipe offline and returns { peak, rms, seconds } (no speakers needed). */
export async function renderRecipe(cue: string, seconds = 4): Promise<{ peak: number; rms: number; seconds: number } | null> {
  const r = RECIPES[cue]
  if (!r) return null
  const ac = new OfflineAudioContext(1, Math.ceil(44100 * seconds), 44100)
  const prev = DEST
  DEST = ac.destination
  try {
    r(ac, 0.01, 0.35)
  } finally {
    DEST = prev
  }
  const buf = await ac.startRendering()
  const d = buf.getChannelData(0)
  let peak = 0
  let sum = 0
  let last = 0
  for (let i = 0; i < d.length; i++) {
    const x = Math.abs(d[i]!)
    if (x > peak) peak = x
    sum += x * x
    if (x > 0.001) last = i
  }
  return { peak: +peak.toFixed(3), rms: +Math.sqrt(sum / d.length).toFixed(4), seconds: +(last / 44100).toFixed(2) }
}

/** A run of short notes, each starting `at` seconds from now (the reward and PIN cues below). */
function tune(notes: readonly (readonly [hz: number, at: number, len: number])[], type: OscillatorType, volume: number) {
  const a = audio()
  if (!a || a.state !== 'running') return
  for (const [hz, at, len] of notes) {
    const o = a.createOscillator()
    o.type = type
    o.frequency.value = hz
    const g = a.createGain()
    const t = a.currentTime + at
    g.gain.setValueAtTime(0.0001, t)
    g.gain.exponentialRampToValueAtTime(0.35 * volume, t + 0.01)
    g.gain.exponentialRampToValueAtTime(0.0001, t + len)
    o.connect(g).connect(a.destination)
    o.start(t)
    o.stop(t + len + 0.05)
  }
}

/** A prize fanfare that grows with rarity (common: two notes; legendary: a full arpeggio and a sparkle). */
export function prizeFanfare(rarity: 'common' | 'rare' | 'legendary') {
  const notes: Record<typeof rarity, [number, number, number][]> = {
    common: [[659, 0, 0.18], [988, 0.12, 0.3]],
    rare: [[523, 0, 0.16], [659, 0.1, 0.16], [784, 0.2, 0.16], [1047, 0.3, 0.4]],
    legendary: [[523, 0, 0.14], [659, 0.08, 0.14], [784, 0.16, 0.14], [1047, 0.24, 0.2], [1319, 0.36, 0.2], [1568, 0.48, 0.6], [2093, 0.62, 0.5]],
  }
  tune(notes[rarity], 'triangle', rarity === 'legendary' ? 0.9 : 0.7)
}

/**
 * WI-13 reveal cue: a collectible is a common fanfare; an act key the rare one (a new act is the big
 * moment); a real prize by its rarity.
 */
export function revealFanfare(kind: 'collectible' | 'key' | 'prize', rarity: 'common' | 'rare' | 'legendary' = 'common') {
  prizeFanfare(kind === 'prize' ? rarity : kind === 'key' ? 'rare' : 'common')
}

/** "Plays earned": two soft bells. */
export function playsChime() {
  tune([[1175, 0, 0.35], [1568, 0.14, 0.5]], 'sine', 0.6)
}

/** PIN pad: a key tick, and a low buzz for a wrong PIN. */
export function pinTick() {
  tune([[1800, 0, 0.03]], 'square', 0.15)
}
export function pinWrong() {
  tune([[180, 0, 0.12], [140, 0.12, 0.22]], 'sawtooth', 0.4)
}

/** The unwrap moment (A3): wrapping paper tearing, a filtered noise whoosh that rises and falls. */
export function unwrapWhoosh() {
  const a = audio()
  if (!a || a.state !== 'running') return
  const n = noise(a, 0.5)
  const f = a.createBiquadFilter()
  f.type = 'bandpass'
  f.Q.value = 1.2
  f.frequency.setValueAtTime(600, a.currentTime)
  f.frequency.exponentialRampToValueAtTime(3200, a.currentTime + 0.22)
  f.frequency.exponentialRampToValueAtTime(900, a.currentTime + 0.45)
  n.connect(f).connect(env(a, 0.5, 0.06, 0.4))
  n.start()
  n.stop(a.currentTime + 0.5)
}

/** The island pops up out of its paper: a quick upward blip and a sparkle. */
export function unwrapPop() {
  tune([[392, 0, 0.12], [784, 0.06, 0.16], [1175, 0.14, 0.3]], 'triangle', 0.7)
}
